import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight, Calendar, Box, Eye, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";

const STORAGE_KEY = "onboarding_tour_completed_v1";

const STEPS = {
  organizer: [
    {
      icon: Sparkles,
      eyebrow: "Étape 1 sur 5",
      title: "Bienvenue chez EventMarket Pro",
      body:
        "Vous êtes connecté en tant qu'organisateur. Ce mini-tour de 90 secondes vous montre comment créer votre premier événement et accueillir des exposants.",
    },
    {
      icon: Calendar,
      eyebrow: "Étape 2 sur 5",
      title: "Créer votre événement",
      body:
        "Depuis le tableau de bord, cliquez sur « Nouvel événement ». Renseignez le titre, la description, les dates, la ville et téléversez quelques photos qui mettent en valeur le lieu.",
    },
    {
      icon: Box,
      eyebrow: "Étape 3 sur 5",
      title: "Ajouter des stands",
      body:
        "Une fois l'événement créé, ajoutez des stands avec leur prix, surface (en m²), quantité disponible et options (électricité, wifi). Les exposants pourront les réserver en quelques clics.",
    },
    {
      icon: CreditCard,
      eyebrow: "Étape 4 sur 5",
      title: "Paiements automatiques",
      body:
        "Les exposants paient en ligne via Stripe (sécurisé, PCI-DSS). Nous prélevons 20% de commission, le reste vous est reversé. Vous recevez un email à chaque réservation.",
    },
    {
      icon: Eye,
      eyebrow: "Étape 5 sur 5",
      title: "Gagner en visibilité",
      body:
        "Boostez votre événement en page d'accueil et dans les recherches. Suivez vos statistiques (vues, taux de remplissage) en temps réel depuis votre tableau de bord.",
      cta: { label: "Créer mon premier événement", to: "/dashboard/events/new", testid: "tour-cta-create-event" },
    },
  ],
  exhibitor: [
    {
      icon: Sparkles,
      eyebrow: "Étape 1 sur 3",
      title: "Bienvenue exposant",
      body:
        "Trouvez les événements où exposer vos produits, réservez un stand en quelques clics et recevez une facture PDF téléchargeable.",
    },
    {
      icon: Calendar,
      eyebrow: "Étape 2 sur 3",
      title: "Parcourir les événements",
      body:
        "Filtrez par région, date, type d'événement, prix de stand. Consultez les fiches détaillées et les avis vérifiés des exposants précédents.",
    },
    {
      icon: Box,
      eyebrow: "Étape 3 sur 3",
      title: "Réserver et préparer",
      body:
        "Choisissez votre stand, payez en ligne, recevez votre confirmation. Tous vos stands réservés sont rassemblés dans « Mes réservations » avec leur facture PDF.",
      cta: { label: "Explorer les événements", to: "/events", testid: "tour-cta-browse" },
    },
  ],
  venue_owner: [
    {
      icon: Sparkles,
      eyebrow: "Étape 1 sur 3",
      title: "Bienvenue propriétaire de lieu",
      body:
        "Mettez votre lieu en location pour des événements ponctuels et générez des revenus complémentaires.",
    },
    {
      icon: Box,
      eyebrow: "Étape 2 sur 3",
      title: "Publier votre lieu",
      body:
        "Renseignez surface, capacité, équipements, photos et tarifs. Plus la fiche est complète, plus les demandes affluent.",
    },
    {
      icon: CreditCard,
      eyebrow: "Étape 3 sur 3",
      title: "Recevoir des réservations",
      body:
        "Les organisateurs vous contactent et réservent directement. Vous gardez la main sur votre calendrier.",
      cta: { label: "Publier mon lieu", to: "/dashboard/venues/new", testid: "tour-cta-create-venue" },
    },
  ],
  provider: [
    {
      icon: Sparkles,
      eyebrow: "Étape 1 sur 2",
      title: "Bienvenue prestataire",
      body:
        "Faites-vous connaître auprès des organisateurs et exposants : traiteur, sonorisation, mobilier, sécurité, communication…",
    },
    {
      icon: Eye,
      eyebrow: "Étape 2 sur 2",
      title: "Compléter votre profil",
      body:
        "Renseignez votre spécialité, vos prestations types, votre zone d'intervention et vos tarifs indicatifs. Plus votre profil est précis, plus vous recevez de demandes qualifiées.",
      cta: { label: "Aller au tableau de bord", to: "/dashboard", testid: "tour-cta-dashboard" },
    },
  ],
};

export default function OnboardingTour({ role, autoStart = true, forceOpen = false }) {
  const steps = STEPS[role] || [];
  const [open, setOpen] = useState(forceOpen);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      return;
    }
    if (!autoStart || steps.length === 0) return;
    const flag = `${STORAGE_KEY}_${role}`;
    if (!localStorage.getItem(flag)) {
      // delay so dashboard renders first
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [role, autoStart, steps.length, forceOpen]);

  const close = (markDone = true) => {
    if (markDone) localStorage.setItem(`${STORAGE_KEY}_${role}`, "1");
    setOpen(false);
    setIdx(0);
  };

  if (!open || steps.length === 0) return null;
  const step = steps[idx];
  const Icon = step.icon;
  const isLast = idx === steps.length - 1;

  return (
    <div
      data-testid="onboarding-tour"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-xl bg-cream border border-ink/10 rounded-md overflow-hidden shadow-2xl">
        {/* Close */}
        <button
          data-testid="tour-close"
          onClick={() => close(true)}
          className="absolute top-4 right-4 p-1.5 rounded hover:bg-ink/10 transition"
          aria-label="Fermer"
        >
          <X className="h-4 w-4 text-ink/60" />
        </button>

        <div className="p-8 lg:p-10 grain">
          <div className="flex items-center gap-2 mb-5">
            <Icon className="h-5 w-5 text-accent" />
            <p className="text-label text-accent">{step.eyebrow}</p>
          </div>
          <h2
            data-testid="tour-step-title"
            className="font-heading text-3xl lg:text-4xl text-ink font-light tracking-editorial leading-tight mb-4"
          >
            {step.title}
          </h2>
          <p className="text-ink/75 font-light leading-relaxed text-[15px] mb-8">
            {step.body}
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mb-7">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i === idx ? "w-8 bg-accent" : i < idx ? "w-4 bg-accent/40" : "w-4 bg-ink/15"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              data-testid="tour-skip"
              onClick={() => close(true)}
              className="text-xs uppercase tracking-wider text-ink/50 hover:text-ink transition"
            >
              Passer le tour
            </button>

            <div className="flex items-center gap-2">
              {idx > 0 && (
                <Button
                  variant="outline"
                  data-testid="tour-prev"
                  onClick={() => setIdx((i) => i - 1)}
                  className="rounded-none border-ink/20 text-ink hover:bg-ink hover:text-cream"
                >
                  Précédent
                </Button>
              )}
              {!isLast ? (
                <Button
                  data-testid="tour-next"
                  onClick={() => setIdx((i) => i + 1)}
                  className="bg-ink hover:bg-ink/85 text-cream rounded-none px-6"
                >
                  Suivant <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : step.cta ? (
                <Link to={step.cta.to} onClick={() => close(true)}>
                  <Button
                    data-testid={step.cta.testid}
                    className="bg-accent hover:bg-accent/90 text-ink rounded-none px-6 font-semibold"
                  >
                    {step.cta.label} <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <Button
                  data-testid="tour-finish"
                  onClick={() => close(true)}
                  className="bg-ink hover:bg-ink/85 text-cream rounded-none px-6"
                >
                  Terminer
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Manual launcher: lets the user re-open the tour anytime. */
export function OnboardingTourTrigger({ role, className = "" }) {
  const [show, setShow] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="onboarding-relaunch"
        onClick={() => {
          localStorage.removeItem(`${STORAGE_KEY}_${role}`);
          setShow(true);
        }}
        className={`text-xs uppercase tracking-wider text-ink/55 hover:text-accent transition ${className}`}
      >
        Revoir le tour
      </button>
      {show && <OnboardingTour role={role} autoStart={false} forceOpen={true} />}
    </>
  );
}
